import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scroll to the piece a shared link names, once it is actually on the page.
 *
 * WHY THIS EXISTS
 * ---------------
 * The Blog shares a single piece as `?view=blog&section=<section>#a-<slug>` — the same
 * link its own index tiles use. Within one section that works by itself, because
 * the browser handles a same-page anchor natively.
 *
 * It does NOT work when the link arrives from somewhere else: the six history
 * pieces that link to the appendix, a link pasted from an email, or a tab switch.
 * The browser resolves the hash while React is still rendering, finds no element
 * with that id, and gives up silently — the reader lands at the top of the right
 * tab instead of on the piece they clicked.
 *
 * So we do it ourselves, after the section has drawn.
 */

// Retries, not milliseconds — a slow machine has long frames. In a background
// tab requestAnimationFrame does not fire at all, so the budget is not consumed.
const MAX_FRAMES = 90;

/**
 * @param {*} readyKey  re-runs when this changes (the current section), so a
 *                      tab switch re-resolves the hash against the new content
 * @param {function} [raf]  requestAnimationFrame (injected for testing)
 * @param {Document} [doc]  document (injected for testing)
 */
export default function useScrollToHash(readyKey, raf, doc) {
  const { hash } = useLocation();

  useEffect(() => {
    const schedule = raf || ((cb) => requestAnimationFrame(cb));
    const d = doc || document;
    if (!hash || hash.length < 2) return undefined;

    const id = decodeURIComponent(hash.slice(1));
    let cancelled = false;
    let frames = 0;

    const attempt = () => {
      if (cancelled) return;
      const el = d.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      frames += 1;
      if (frames > MAX_FRAMES) {
        // Says so rather than failing silently — a shared link that stops
        // working should leave something to read.
        // eslint-disable-next-line no-console
        console.warn(`[footprint] nothing on the page with id "${id}"`);
        return;
      }
      schedule(attempt);
    };

    schedule(attempt);
    return () => {
      cancelled = true;
    };
  }, [hash, readyKey, raf, doc]);
}
